import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const RESOURCE_CATEGORIES = [
  "Fundraising",
  "Growth",
  "Hiring",
  "Leadership",
  "Management",
  "Marketing",
  "Pitching",
  "Product",
  "Strategy",
  "Culture",
  "Tools",
  "Newsletters",
  "Jamaica Resources",
  "Reading List",
] as const;

export const RESOURCE_FORMATS = [
  "Blog",
  "YouTube",
  "LinkedIn",
  "Podcast",
  "Vimeo",
  "Notion",
  "Newsletter",
  "Classroom",
  "WhatsApp",
  "Resource",
  "Book",
] as const;

export type ResourceCategory = typeof RESOURCE_CATEGORIES[number];
export type ResourceFormat = typeof RESOURCE_FORMATS[number];

export interface Resource {
  id: string;
  title: string;
  category: string;
  format: string;
  link: string;
  created_at: string;
  created_by: string | null;
}

export function useResources(category?: string) {
  return useQuery({
    queryKey: ["resources", category],
    queryFn: async () => {
      let query = supabase
        .from("resources")
        .select("*")
        .order("category")
        .order("title");

      if (category && category !== "all") {
        query = query.eq("category", category);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Resource[];
    },
  });
}

export function useCreateResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (resource: {
      title: string;
      category: string;
      format: string;
      link: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from("resources")
        .insert({
          ...resource,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resources"] });
      toast({
        title: "Resource added",
        description: "The resource has been added successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to add resource. Please try again.",
        variant: "destructive",
      });
      console.error("Error creating resource:", error);
    },
  });
}

export function useDeleteResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("resources")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resources"] });
      toast({
        title: "Resource deleted",
        description: "The resource has been removed.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete resource. Please try again.",
        variant: "destructive",
      });
      console.error("Error deleting resource:", error);
    },
  });
}
